import { useMutation } from '@tanstack/react-query'
import { useAuth } from '../providers'
import { v1Client } from '../api'
import { notifyCheckoutOpened, refreshBillingAndWait } from '../store/global-billing'

/** Paid plans a Stripe checkout can target; defaults to 'pro' when omitted. */
export type CheckoutPlan = 'pro' | 'lite' | 'pro2' | 'max'

export interface CheckoutOptions {
  plan?: CheckoutPlan
  /**
   * The v2 trial converting to `plan` (lite/pro2 only); the checkout collects a card. Already
   * on that trial, it only saves the card and the trial keeps its end date (fulfillV2TrialCheckout).
   */
  trial?: boolean
}

/**
 * The server's human-readable message from an error payload ({ success: false,
 * error: string }), or null. Thrown error messages reach users verbatim (and
 * isPaymentMethodRequiredError matches on them), so prefer the server's text
 * over stringified JSON.
 */
export const serverErrorMessage = (error: unknown): string | null => {
  if (error && typeof error === 'object' && 'error' in error && typeof error.error === 'string') {
    return error.error
  }
  return null
}

/**
 * Hook to create a Stripe checkout session and redirect the user to payment setup
 */
export const useCheckout = (mode: 'subscription' | 'setup' = 'subscription') => {
  const { getAuthToken } = useAuth()

  return useMutation({
    mutationFn: async (input?: CheckoutPlan | CheckoutOptions) => {
      const { plan, trial } = typeof input === 'string' ? { plan: input, trial: undefined } : (input ?? {})
      // default to subscription mode, setup is only for managing payment methods
      const token = await getAuthToken()
      const response = await v1Client.POST('/billing/checkout', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: {
          mode,
          ...(plan ? { plan } : {}),
          ...(trial ? { trial } : {}),
        },
      })

      if (response.error) {
        throw new Error(
          serverErrorMessage(response.error) ?? `Failed to create checkout session: ${JSON.stringify(response.error)}`,
        )
      }

      return response.data.data
    },
    onSuccess: (data) => {
      // Track the checkout initiation
      window.ampltd.trackEvent('Subscription', {
        action: 'checkout initiated',
        source: 'billing',
      })

      // Open the checkout URL in the user's default browser
      window.open(data.checkoutUrl, '_blank')

      // Every checkout (upgrade, trial, payment-method setup) passes through here,
      // so this is the one place that knows a purchase is in flight.
      notifyCheckoutOpened()
    },
    onError: (error) => {
      console.error('Checkout error:', error)
      window.ampltd.trackEvent('Subscription', {
        action: 'checkout error',
        error: error instanceof Error ? error.message : 'Unknown error',
      })
    },
  })
}

/**
 * Hook to update the user's billing plan
 */
export type UpdatePlanTarget = 'pro' | 'free' | 'pro-trial' | 'lite' | 'pro2' | 'max' | 'lite-trial' | 'pro2-trial'

/**
 * Whether a plan mutation was refused for lack of a card on file — the cue for
 * callers to fall back to a Stripe checkout, which collects one.
 */
export const isPaymentMethodRequiredError = (error: unknown): boolean =>
  error instanceof Error && /payment method/i.test(error.message)

/** Whether a trial start was refused because its card already started another account's free trial. */
export const isCardUsedForTrialError = (error: unknown): boolean =>
  error instanceof Error && /already been used for a free trial/i.test(error.message)

export const useUpdatePlan = () => {
  const { getAuthToken } = useAuth()

  return useMutation({
    mutationFn: async (plan: UpdatePlanTarget) => {
      const token = await getAuthToken()
      const response = await v1Client.POST('/billing/plan', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: {
          plan,
        },
      })

      if (response.error) {
        throw new Error(
          serverErrorMessage(response.error) ?? `Failed to update plan: ${JSON.stringify(response.error)}`,
        )
      }

      return { ...response.data, billingRefreshed: await refreshBillingAndWait() }
    },
    onSuccess: (_data, variables) => {
      window.ampltd.trackEvent('Subscription', {
        action: 'plan updated',
        plan: variables,
      })
    },
    onError: (error) => {
      console.error('Plan update error:', error)
      window.ampltd.trackEvent('Subscription', {
        action: 'plan update error',
        error: error instanceof Error ? error.message : 'Unknown error',
      })
    },
  })
}

/**
 * Hook to open the Stripe billing portal
 */
export const useManageBilling = () => {
  return {
    openPortal: (portalUrl: string | null) => {
      if (!portalUrl) {
        console.error('No portal URL available')
        return
      }

      window.ampltd.trackEvent('Subscription', {
        action: 'billing portal opened',
        source: 'settings',
      })

      window.open(portalUrl, '_blank')
    },
  }
}
